import { FreeShippingReason } from '@faire/web-api--source/indigofair/data/FreeShippingReason';
import { IApplicableMembershipBenefits } from '@faire/web-api--source/indigofair/data/IApplicableMembershipBenefits';
import { IBrandReviewsSummary } from '@faire/web-api--source/indigofair/data/IBrandReviewsSummary';
import { IBrandSearchQueryUXMessaging } from '@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging';
import { IBrandTilePromoLabel } from '@faire/web-api--source/indigofair/data/IBrandTilePromoLabel';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IInformationMessage } from '@faire/web-api--source/indigofair/data/IInformationMessage';
import { IInsiderLabel } from '@faire/web-api--source/indigofair/data/IInsiderLabel';
import { ISeasonYear } from '@faire/web-api--source/indigofair/data/ISeasonYear';
import { ShowLocationBadging } from '@faire/web-api--source/indigofair/data/ShowLocationBadging';

export namespace ILeanBrandTile {
  export namespace IBrand {
    /**
     * Return an instance of IBrand with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IBrand>): IBrand =>
      ({
        highlighted_apparel_seasons_for_promotional_event: [],
        settings_to_assign_on_view: [],
        product_images: [],
        ...(partial ?? {}),
      }) as IBrand;
  }

  export interface IBrand {
    /**
     * @protobufOption (.faire.common.tokenType) = BRAND
     */
    token: string | undefined;
    name: string | undefined;
    description: string | undefined;
    ships_from_country: string | undefined;
    is_following: boolean | undefined;
    is_first_order: boolean | undefined;
    has_new_products: boolean | undefined;
    profile_image: IImage | undefined;
    squared_image: IImage | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    /**
     * Currently only used in product search, catnav in brandsByTokens in search service
     */
    amount_in_cart: IFaireMoney | undefined;
    /**
     * @deprecated
     */
    free_shipping_reason: keyof typeof FreeShippingReason | undefined;
    insider_benefits: IApplicableMembershipBenefits | undefined;
    insider_label: IInsiderLabel | undefined;
    brand_reviews_summary: IBrandReviewsSummary | undefined;
    is_highlighted_brand_for_promotional_event: boolean | undefined;
    /**
     * True if the brand qualifies as a Top Shop for the current cycle
     */
    is_top_shop: boolean | undefined;
    /**
     * This is for providing a list of Seasons and Years associated with a Brand's RSVP feedback
     */
    highlighted_apparel_seasons_for_promotional_event: Array<ISeasonYear>;
    zip_code_protection_enabled: boolean | undefined;
    /**
     * @deprecated
     */
    based_in_country: string | undefined;
    based_in: string | undefined;
    hide_chat: boolean | undefined;
    show_location_badging: keyof typeof ShowLocationBadging | undefined;
    /**
     * True if the brand qualifies as a showroom brand
     */
    is_showroom: boolean | undefined;
    active_products_count: number | undefined;
    based_in_country_message: IBrandSearchQueryUXMessaging | undefined;
    /**
     * Feature was rolled back, and there is no replacement field
     * @deprecated
     */
    region_flag_image: IImage | undefined;
    forbids_online_only_retailers: boolean | undefined;
    /**
     * Formatted and localized avg brand review rating, i.e "4.8" in english or "4,8" in french.
     */
    avg_brand_review_rating: string | undefined;
    settings_to_assign_on_view: Array<string>;
    product_images: Array<IImage>;
    /**
     * Badge to show next to the Insider label
     */
    status_badge: IInformationMessage | undefined;
    impression_id: string | undefined;
  }

  /**
   * Return an instance of ILeanBrandTile with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<ILeanBrandTile>): ILeanBrandTile =>
    ({
      images: [],
      ...(partial ?? {}),
    }) as ILeanBrandTile;
}

export interface ILeanBrandTile {
  brand: ILeanBrandTile.IBrand | undefined;
  promo_text: string | undefined;
  images: Array<IImage>;
  lead_time_text: string | undefined;
  featured_brand_promo_label: IBrandTilePromoLabel | undefined;
  brand_promo_label: IBrandTilePromoLabel | undefined;
}
